import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAdmin } from "../../../hooks/useAdmin";
import { useManageTripPublication } from "../../tripPublication/hooks/useManageTripPublication";
import { getTrackingApiUrl } from "../../../../tracking/config";

const EMPTY_PARTNER_SUMMARY = {
  establishmentId: "",
  publicationId: "",
  period: "last_30_days",
  generatedAt: null,
  stats: {
    funnel: [],
    engagement: {
      bounceRate: 0,
      avgTimeSpent: 0,
      avgTimeSpentPrevious: 0,
      bounceRatePrevious: 0,
      favoritesCount: 0,
      reviewReadRate: 0,
    },
    evolution: [],
    geoDistribution: [],
  },
};

const PERIOD_OPTIONS = [
  { value: "last_7_days", text: "7 derniers jours", index: 0 },
  { value: "last_30_days", text: "30 derniers jours", index: 1 },
  { value: "last_90_days", text: "90 derniers jours", index: 2 },
];

const normalizeNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const normalizePartnerSummary = (summary, fallback = EMPTY_PARTNER_SUMMARY) => {
  const rawSummary = summary && typeof summary === "object" ? summary : {};
  const rawStats = rawSummary.stats && typeof rawSummary.stats === "object" ? rawSummary.stats : {};
  const rawEngagement =
    rawStats.engagement && typeof rawStats.engagement === "object" ? rawStats.engagement : {};

  return {
    ...fallback,
    ...rawSummary,
    establishmentId: rawSummary.establishmentId || fallback.establishmentId,
    publicationId: rawSummary.publicationId || fallback.publicationId,
    period: rawSummary.period || fallback.period,
    generatedAt: rawSummary.generatedAt || fallback.generatedAt,
    stats: {
      funnel: Array.isArray(rawStats.funnel) ? rawStats.funnel : fallback.stats.funnel,
      engagement: {
        bounceRate: normalizeNumber(rawEngagement.bounceRate),
        bounceRatePrevious: normalizeNumber(rawEngagement.bounceRatePrevious),
        avgTimeSpent: normalizeNumber(rawEngagement.avgTimeSpent),
        avgTimeSpentPrevious: normalizeNumber(rawEngagement.avgTimeSpentPrevious),
        favoritesCount: normalizeNumber(rawEngagement.favoritesCount),
        reviewReadRate: normalizeNumber(rawEngagement.reviewReadRate),
      },
      evolution: Array.isArray(rawStats.evolution) ? rawStats.evolution : fallback.stats.evolution,
      geoDistribution: Array.isArray(rawStats.geoDistribution)
        ? rawStats.geoDistribution
        : fallback.stats.geoDistribution,
    },
  };
};

const buildSummaryUrl = (baseUrl, queryParams) => {
  const normalizedBaseUrl = String(baseUrl || "").replace(/\/$/, "");
  const url = new URL("/analytics/summary", `${normalizedBaseUrl}/`);

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === "all") {
      return;
    }

    url.searchParams.set(key, String(value));
  });

  return url.toString();
};

// establishmentId now travels in the POST body as establishmentIds (array) —
// the API no longer accepts it as a query param. "all" means "no filter",
// which the backend expects as an omitted/empty array.
const buildSummaryBody = (establishmentId) => ({
  establishmentIds: establishmentId && establishmentId !== "all" ? [establishmentId] : [],
});

export function usePartnerAnalyticsDashboard() {
  const { organization, accessToken } = useAdmin();
  const { establishmentFilterOptions, trips, setSelectedEstablishment } =
    useManageTripPublication();

  const trackingApiUrl = useMemo(() => getTrackingApiUrl(), []);
  const requestSeqRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  const [filters, setFilters] = useState({
    establishmentId: "all",
    publicationId: "all",
    period: EMPTY_PARTNER_SUMMARY.period,
  });
  const [partnerSummary, setPartnerSummary] = useState(EMPTY_PARTNER_SUMMARY);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date());

  const queryParams = useMemo(
    () => ({
      publicationId: filters.publicationId === "all" ? undefined : filters.publicationId,
      period: filters.period,
    }),
    [filters.period, filters.publicationId]
  );

  const publicationOptions = useMemo(() => {
    const seen = new Set();
    const dynamicOptions = (trips || [])
      .filter((item) => item?.id && item?.trip?.reference)
      .filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      })
      .map((item, index) => ({
        index: index + 1,
        value: item.id,
        text: item.trip.reference,
      }));

    return [
      {
        index: 0,
        value: "all",
        text: "Toutes les references",
      },
      ...dynamicOptions,
    ];
  }, [trips]);

  const options = useMemo(
    () => ({
      establishments: establishmentFilterOptions,
      publications: publicationOptions,
      periods: PERIOD_OPTIONS,
    }),
    [establishmentFilterOptions, publicationOptions]
  );

  const fetchPartnerSummary = useCallback(async () => {
    if (!trackingApiUrl) {
      setError("TRACKING_API_URL is not configured.");
      setIsLoading(false);
      setIsFetching(false);
      return;
    }

    if (!organization?.entityId || !accessToken) {
      return;
    }

    const requestId = ++requestSeqRef.current;
    const isInitialRequest = isInitialLoadRef.current;

    setError(null);
    setIsFetching(true);
    if (isInitialRequest) {
      setIsLoading(true);
    }

    try {
      const url = buildSummaryUrl(trackingApiUrl, queryParams);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(buildSummaryBody(filters.establishmentId)),
      });

      const payload = await response.json().catch(() => null);

      if (requestId !== requestSeqRef.current) {
        return;
      }

      if (!response.ok) {
        throw new Error(
          payload?.error || `Failed to load dashboard summary (${response.status})`
        );
      }

      if (payload?.error) {
        throw new Error(payload.error);
      }

      const partnerSummaryPayload = payload?.partnerSummary || payload;
      const nextSummary = normalizePartnerSummary(partnerSummaryPayload, {
        ...EMPTY_PARTNER_SUMMARY,
        establishmentId: filters.establishmentId === "all" ? "" : filters.establishmentId,
        publicationId: queryParams.publicationId || "",
        period: queryParams.period || EMPTY_PARTNER_SUMMARY.period,
      });

      setPartnerSummary(nextSummary);
      setLastRefreshedAt(new Date());
      setError(null);
    } catch (dashboardError) {
      if (requestId !== requestSeqRef.current) {
        return;
      }

      setError(dashboardError?.message || "Failed to load dashboard summary");
    } finally {
      if (requestId === requestSeqRef.current) {
        isInitialLoadRef.current = false;
        setIsLoading(false);
        setIsFetching(false);
      }
    }
  }, [accessToken, filters.establishmentId, queryParams, trackingApiUrl, organization?.entityId]);

  // Keep the latest fetchPartnerSummary available without making it a dependency
  // of the effect below — filters must NOT trigger an auto-fetch, only mount
  // (once auth/config are ready) and the "Mettre a jour" button should.
  const fetchPartnerSummaryRef = useRef(fetchPartnerSummary);
  useEffect(() => {
    fetchPartnerSummaryRef.current = fetchPartnerSummary;
  }, [fetchPartnerSummary]);

  useEffect(() => {
    void fetchPartnerSummaryRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, organization?.entityId, trackingApiUrl]);

  const kpis = useMemo(
    () => [
      {
        key: "bounce",
        label: "Qualité de l'annonce",
        rawValue: partnerSummary.stats.engagement.bounceRate,
        previousRawValue: partnerSummary.stats.engagement.bounceRatePrevious,
        value: `${partnerSummary.stats.engagement.bounceRate}`,
        unit: "%",
        hint: "Taux cible < 20%",
      },
      {
        key: "review",
        label: "Avis clients consultés",
        rawValue: partnerSummary.stats.engagement.reviewReadRate,
        value: `${partnerSummary.stats.engagement.reviewReadRate}`,
        unit: "%",
        hint: "Engagement optimal",
      },
      {
        key: "favorites",
        label: "Favoris total",
        rawValue: partnerSummary.stats.engagement.favoritesCount,
        value: `${partnerSummary.stats.engagement.favoritesCount}`,
        hint: "Nombre total d'ajouts",
      },
      {
        key: "time",
        label: "Temps passé sur l'annonce",
        rawValue: partnerSummary.stats.engagement.avgTimeSpent,
        previousRawValue: partnerSummary.stats.engagement.avgTimeSpentPrevious,
        value: `${partnerSummary.stats.engagement.avgTimeSpent}`,
        unit: "s",
        hint: "Par session utilisateur",
      },
    ],
    [partnerSummary]
  );

  useEffect(() => {
    const isCurrentPublicationAvailable = options.publications.some(
      (option) => option.value === filters.publicationId
    );

    if (!isCurrentPublicationAvailable) {
      setFilters((previous) => ({ ...previous, publicationId: "all" }));
    }
  }, [filters.publicationId, options.publications]);

  const setEstablishmentId = useCallback(
    (_, option) => {
      const nextEstablishmentId = option?.value || "all";
      setSelectedEstablishment(nextEstablishmentId);
      setFilters((previous) => ({
        ...previous,
        establishmentId: nextEstablishmentId,
        publicationId: "all",
      }));
    },
    [setSelectedEstablishment]
  );

  const setPublicationId = useCallback((_, option) => {
    setFilters((previous) => ({
      ...previous,
      publicationId: option?.value || previous.publicationId,
    }));
  }, []);

  const setPeriod = useCallback((_, option) => {
    setFilters((previous) => ({
      ...previous,
      period: option?.value || previous.period,
    }));
  }, []);

  const refresh = useCallback(() => {
    void fetchPartnerSummary();
  }, [fetchPartnerSummary]);

  return {
    partnerSummary,
    kpis,
    options,
    filters,
    actions: {
      setEstablishmentId,
      setPublicationId,
      setPeriod,
      refresh,
    },
    uiState: {
      isLoading,
      isFetching,
      error,
      lastRefreshedAt,
    },
  };
}

export default usePartnerAnalyticsDashboard;
